'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Alternating image and text rows, each image cut with the signature leaf mask
 * (`border-radius: 0 50px`).
 *
 * <p>Rows alternate by position: the first row puts its image on the left,
 * the next on the right, and so on. Source order is always image then text, so
 * a screen reader and a phone read each row the same way regardless of the
 * desktop layout. At 768 px and below every row stacks.
 */
export interface SpatialKitchenFeatureRow {
  eyebrow?: Localized;
  title?: Localized;
  body?: Localized;
  image?: SkImageRef;
  linkLabel?: Localized;
  linkHref?: string;
  /** Short supporting points, shown as a quiet list under the body. */
  points?: Localized[];
}

export interface SpatialKitchenFeatureRowsProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  rows?: SpatialKitchenFeatureRow[];
  /** Start with the image on the right instead of the left. */
  startRight?: boolean;
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-rows__list{display:grid;gap:clamp(3rem,2rem + 4vw,6rem)}
.sk-row{display:grid;gap:1.75rem;align-items:center}
.sk-row__media{aspect-ratio:4/3;width:100%;box-shadow:0 14px 36px rgba(32,38,34,.1)}
.sk-row__text{display:grid;gap:1rem;max-width:34rem}
.sk-row__points{display:grid;gap:.5rem;margin:.25rem 0 0;padding:0;list-style:none}
.sk-row__points li{position:relative;padding-left:1.25rem;color:var(--sk-muted)}
.sk-row__points li::before{content:"";position:absolute;left:0;top:.7em;width:.5rem;height:1px;background:var(--sk-sage-deep)}
@media (min-width:769px){
  .sk-row{grid-template-columns:minmax(0,7fr) minmax(0,5fr);gap:clamp(2.5rem,5vw,6rem)}
  .sk-row--flip .sk-row__media{order:2}
  .sk-row--flip{grid-template-columns:minmax(0,5fr) minmax(0,7fr)}
}
`;

export function SpatialKitchenFeatureRows(props: SpatialKitchenFeatureRowsProps) {
  const rows = items<SpatialKitchenFeatureRow>(props.rows);
  const title = text(props.title);
  const eyebrow = text(props.eyebrow);
  const intro = text(props.intro);
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const RowTitle = title ? childLevel(level) : level;
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-rows sk-section ${surfaceClass(props.surface, 'white')}`}
      {...(title ? { 'aria-labelledby': titleId } : {})}
    >
      <SkBaseStyles />
      <SkStyles id="feature-rows" css={CSS} />
      <div className="sk-container">
        {(title || intro) && (
          <div className="sk-section-head">
            {eyebrow && <p className="sk-eyebrow">{eyebrow}</p>}
            {title && (
              <Title id={titleId} className="sk-heading sk-h2">
                {title}
              </Title>
            )}
            {intro && <p className="sk-lead">{intro}</p>}
          </div>
        )}
        <div className="sk-rows__list">
          {rows.map((row, index) => {
            const flip = (index % 2 === 1) !== Boolean(props.startRight);
            const rowTitle = text(row.title);
            const linkLabel = text(row.linkLabel);
            const points = (row.points ?? []).map((point) => text(point)).filter(Boolean);
            return (
              <article key={`${rowTitle}-${index}`} className={`sk-row${flip ? ' sk-row--flip' : ''}`}>
                <div className="sk-row__media sk-media sk-leaf">
                  <SkImage image={row.image} sizes="(min-width: 769px) 58vw, 100vw" />
                </div>
                <div className="sk-row__text">
                  {text(row.eyebrow) && <p className="sk-eyebrow">{text(row.eyebrow)}</p>}
                  {rowTitle && <RowTitle className="sk-heading sk-h2">{rowTitle}</RowTitle>}
                  {text(row.body) && <p className="sk-body">{text(row.body)}</p>}
                  {points.length > 0 && (
                    <ul className="sk-row__points">
                      {points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  )}
                  {linkLabel && row.linkHref && (
                    <p style={{ margin: '.5rem 0 0' }}>
                      <SkLink href={row.linkHref} className="sk-textlink">
                        {linkLabel}
                        <Icon name="arrow-right" size={18} />
                      </SkLink>
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </BlockRoot>
  );
}
